import React from 'react';
import { Phone, BookOpen, Users, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const resources = [
  {
    category: 'Crisis Support',
    icon: Phone,
    color: 'bg-red-100 text-red-600',
    items: [
      {
        title: 'National Suicide Prevention Lifeline',
        description: '24/7 crisis support',
        contact: '988',
        link: 'https://988lifeline.org/'
      },
      {
        title: 'Crisis Text Line',
        description: 'Text support available 24/7',
        contact: 'Text HOME to 741741',
        link: 'https://www.crisistextline.org/'
      },
      {
        title: 'SAMHSA National Helpline',
        description: 'Mental health and substance abuse support',
        contact: '1-800-662-4357',
        link: 'https://www.samhsa.gov/find-help/national-helpline'
      }
    ]
  },
  {
    category: 'Grief Support Groups',
    icon: Users,
    color: 'bg-blue-100 text-blue-600',
    items: [
      {
        title: 'GriefShare',
        description: 'Faith-based grief recovery support groups',
        contact: 'Find a local group',
        link: 'https://www.griefshare.org/'
      },
      {
        title: 'The Compassionate Friends',
        description: 'Support for bereaved families',
        contact: 'Online and in-person meetings',
        link: 'https://www.compassionatefriends.org/'
      },
      {
        title: 'Modern Loss',
        description: 'Community and resources for navigating grief',
        contact: 'Online community',
        link: 'https://modernloss.com/'
      }
    ]
  },
  {
    category: 'Recommended Reading',
    icon: BookOpen,
    color: 'bg-green-100 text-green-600',
    items: [
      {
        title: 'On Grief and Grieving',
        description: 'By Elisabeth Kübler-Ross and David Kessler',
        contact: 'Essential reading on the grief process'
      },
      {
        title: 'The Year of Magical Thinking',
        description: 'By Joan Didion',
        contact: 'A personal memoir of grief and loss'
      },
      {
        title: 'It\'s OK That You\'re Not OK',
        description: 'By Megan Devine',
        contact: 'Meeting grief without platitudes'
      }
    ]
  }
];

const Resources = () => {
  return (
    <section id="resources" className="py-20 bg-white relative overflow-hidden">
      {/* Decorative background image */}
      <div className="absolute right-0 top-0 w-1/3 h-full opacity-10 pointer-events-none">
        <img 
          src="https://images.unsplash.com/photo-1518495973542-4542c06a5843?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxoZWFsaW5nfGVufDB8fHx8MTc3MzM5MTYyOXww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Support & Resources
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            You don't have to go through this alone. Here are trusted resources to help you 
            navigate your grief journey.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {resources.map((resource, idx) => {
            const Icon = resource.icon;
            return (
              <Card key={idx} className="hover:shadow-xl transition-shadow duration-300 border-2">
                <CardHeader>
                  <div className={`w-12 h-12 rounded-lg ${resource.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-2xl">{resource.category}</CardTitle>
                  <CardDescription>Professional support when you need it most</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {resource.items.map((item, itemIdx) => (
                      <div key={itemIdx} className="pb-6 border-b border-gray-200 last:border-0 last:pb-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-semibold text-gray-900">{item.title}</h4>
                          {item.link && (
                            <a 
                              href={item.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 transition-colors"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                        <p className="text-sm font-medium text-gray-900 mt-2">{item.contact}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div id="support" className="mt-16 bg-gradient-to-br from-blue-600 to-teal-600 rounded-2xl overflow-hidden relative">
          {/* Background image overlay */}
          <div className="absolute inset-0 opacity-20">
            <img 
              src="https://images.unsplash.com/photo-1584515933487-779824d29309?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxjb21mb3J0fGVufDB8fHx8MTc3MzM5MTYyNXww&ixlib=rb-4.1.0&q=85"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="relative z-10 p-8 md:p-12 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">Need Immediate Support?</h3>
            <p className="text-xl text-blue-50 mb-6 max-w-2xl mx-auto">
              If you're in crisis or need someone to talk to right now, help is available 24/7.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="tel:988"
                className="px-8 py-4 bg-white text-blue-600 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors duration-200 shadow-lg"
              >
                Call 988
              </a>
              <a 
                href="sms:741741&body=HOME"
                className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-bold text-lg hover:bg-white/10 transition-colors duration-200"
              >
                Text HOME to 741741
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Resources;
